/**
 * Auth Event Emitter
 * 
 * Simple event emitter for auth state changes.
 * Used by AuthProvider to emit events and by plugins to subscribe.
 * 
 * ## Events
 * 
 * - `login` - Fired after successful login
 * - `logout` - Fired after logout
 * - `sessionRestore` - Fired after session is restored from refresh token
 * - `setupRequired` - Fired by apiClient on a 403 ACCOUNT_SETUP_REQUIRED (the
 *   pre-enrollment session gate refused the account); AuthProvider signs the
 *   member out locally and points them at sign-in → account setup
 * 
 * ## Usage
 * 
 * ```typescript
 * // Subscribe to auth changes
 * const unsubscribe = authEvents.on('login', (userId) => {
 *   console.log('User logged in:', userId);
 * });
 * 
 * // Emit auth event (from AuthProvider)
 * authEvents.emit('login', userId);
 * 
 * // Cleanup
 * unsubscribe();
 * ```
 * 
 * @module auth/events
 */

type AuthEventType = 'login' | 'logout' | 'sessionRestore' | 'setupRequired';
type UserId = string | number | null;
type AuthEventCallback = (userId?: UserId) => void;

class AuthEventEmitter {
  private listeners: Map<AuthEventType, Set<AuthEventCallback>> = new Map();

  /**
   * Subscribe to an auth event
   * 
   * @param event - Event type to listen for
   * @param callback - Function to call when event fires
   * @returns Unsubscribe function
   */
  on(event: AuthEventType, callback: AuthEventCallback): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    
    this.listeners.get(event)!.add(callback);
    
    // Return unsubscribe function
    return () => {
      this.listeners.get(event)?.delete(callback);
    };
  }

  /**
   * Emit an auth event
   * 
   * @param event - Event type to emit
   * @param userId - Optional user ID (for login/sessionRestore)
   */
  emit(event: AuthEventType, userId?: UserId): void {
    const callbacks = this.listeners.get(event);
    
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(userId);
        } catch (error) {
          console.error(`[AuthEvents] Error in ${event} callback:`, error);
        }
      });
    }
  }

  /**
   * Remove all listeners (for testing)
   */
  clear(): void {
    this.listeners.clear();
  }
}

/**
 * Singleton auth event emitter
 * 
 * Import this to subscribe to or emit auth events.
 */
export const authEvents = new AuthEventEmitter();
